import { useQuery } from '@tanstack/react-query';
import { authedFetch, UnauthorizedError } from '@apis/auth';
import { localDateToYmd, ymdToLocalDate } from '../utils/dateRules';

/**
 * Days the selected entity (tag) was absent from the site within the visible
 * report window. The picker disables these days so the user can't open a
 * report on a day with no presence data.
 *
 * Backed by the BFF route `/api/sites/:siteId/tags/:tagId/absent-dates`, which
 * proxies Trusstor's `getTagsAbsenceDates` against the
 * `DailyTagStatusesSummary` aggregation — distinct from the raw archive route
 * feed, so simulator tags with raw presence but no qualifying daily summary
 * still appear here.
 */

interface Params {
  siteId: string;
  tagId: string | null;
  /**
   * Window bounds as TZ-NAIVE calendar days — Dates whose browser-local
   * y/m/d already spell the project's day (`tzCalendarDayAsLocal`, the same
   * convention `react-day-picker` and every other picker value here uses).
   * They are NOT instants, so they must be read field-wise via
   * `localDateToYmd`; running them through `formatYmd(date, tz)` re-projects
   * a local-midnight instant into the project zone and shifts the day, which
   * ended the absent-dates window a day early and left the picker offering
   * today with no presence data behind it.
   */
  fromDate: Date;
  toDate: Date;
}

async function fetchAbsentDates(
  siteId: string,
  tagId: string,
  fromDate: string,
  toDate: string,
  signal?: AbortSignal,
): Promise<Date[]> {
  const qs = new URLSearchParams({ fromDate, toDate }).toString();
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/tags/${encodeURIComponent(tagId)}/absent-dates?${qs}`,
    { signal },
  );
  if (!res.ok) {
    if (res.status === 401) throw new UnauthorizedError();
    throw new Error(`absent-dates request failed: ${res.status}`);
  }
  const dates = (await res.json()) as string[];
  return dates.map(ymdToLocalDate);
}

export function useAbsentDates({ siteId, tagId, fromDate, toDate }: Params) {
  const from = localDateToYmd(fromDate);
  const to = localDateToYmd(toDate);
  return useQuery<Date[]>({
    queryKey: ['absentDates', siteId, tagId, from, to],
    queryFn: ({ signal }) => fetchAbsentDates(siteId, tagId!, from, to, signal),
    enabled: !!siteId && !!tagId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
